// The words of /tools/positions (components/positions/, pages/positionsGame.ts, domain/positions.ts) and of the
// engine card it shows after an answer (components/engine/EngineCard.tsx).
import { words } from "../services/i18n.ts";

const s = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const t = words({
    en: {
        // The exercises
        findTitle: "Find the move",
        findSummary: "The position before a serious error of yours. Find a better move than the one you played.",
        findPrompt: "Find a better move than the one you played.",
        convertTitle: "Keep the advantage",
        convertSummary: "A position you were winning and gave away. Keep it for five moves against a stronger partner.",
        convertPrompt: "You are winning. Keep it for five moves.",
        openingTitle: "Openings gone wrong",
        openingSummary: "The moment your opening went wrong, with your game played up to it. What should you have played?",
        openingPrompt: "Your opening went wrong here. What do you play?",

        // The picker
        yourPositions: "Your positions",
        pickerLead: "The moments of your own analysed games where it went wrong. Each one comes back until you get it right twice.",
        nothingYet: "Nothing to train yet: these come from your analysed games.",
        analyseGames: "Analyse your games",
        ready: (n: number) => `${n} ready`,
        comingBack: (n: number) => `${n} coming back`,
        done: (n: number) => `${n} done`,
        withClock: "With the clock you had in the game",
        start: (n: number) => `Start ${n || ""} ${s(n, "position", "positions")}`,

        // The phases of a game
        phaseAll: "All",
        phaseOpening: "Opening",
        phaseMiddlegame: "Middlegame",
        phaseEndgame: "Endgame",
        phaseTitle: "Only the positions of this phase of the game",

        // The moment
        engineScoreTitle: "The engine's score in the game, from White's side",
        better: (side: string) => `${side} is better`,
        level: "Level",
        youAre: (side: string) => `you are ${side}`,
        clockTitle: "Your clock in the game, before the move",
        onYourClock: (clock: string) => `${clock} on your clock`,
        thinking: (seconds: number) => `Thinking ${seconds} s`,
        of: (index: number, total: number) => `${index} of ${total}`,
        toMove: (side: string) => `${side} to move.`,
        againstPartner: (partner: string) => ` Against the ${partner.toLowerCase()} partner.`,
        movesLeft: "Moves left",
        yourClock: "Your clock",
        lookingAtIt: "Looking at it…",
        tryAgain: (n: number) => `Try again on the board: ${n} ${s(n, "try", "tries")} left.`,
        showSolution: "Show the solution",
        found: "Found",
        close: "Close",
        missed: "Missed",
        tries: (n: number) => `${n} tries`,
        thought: (seconds: string) => `thought ${seconds} s`,
        youPlayed: "You played",
        givenAway: (pawns: string) => `(${pawns} given away)`,
        best: "Best",
        endedAt: "Ended at",
        hadToStay: (pawns: string) => `(it had to stay ${pawns} or more in your favour)`,
        legend: "On the board: green is the best move, red the one of the game, blue yours. Move the pieces to look around: the engine follows.",
        seeRound: "See the round",
        nextPosition: "Next position",
        tryItAgain: "Try it again",
        backToPosition: "Back to the position",
        addToStudyList: "Add the line to my study list",
        leaveRound: "Leave the round",
        roundOver: (title: string) => `${title}: round over`,
        foundOf: (solved: number, total: number, xp: number) => `${solved} of ${total} found. +${xp} XP.`,
        comeBack: "The ones you missed come back tomorrow; the ones you found, in three days. Two in a row and they are done.",
        anotherRound: "Another round",
        anotherExercise: "Choose another exercise",
        thisPosition: "this position",
        reading: "Reading your positions…",
        signedOutLead: "The moments of your own analysed games where it went wrong, as exercises.",
        logIn: "Log in",
        toTrain: " to train yours.",

        // The sentences of domain/positions.ts
        yourOpponent: "your opponent",
        context: (who: string, when: string | null, speed: string | null) =>
            `vs ${who}${when ? ` · ${when}` : ""}${speed ? ` · ${speed}` : ""}`,
        tookIn: (seconds: string) => ` in ${seconds} s`,
        withoutLooking: ", without looking",
        longThink: ", after a long think",
        shortOfTime: ", short of time",
        notSeen: "and your opponent did not see it",
        scoreWent: (from: string, to: string) => `and the score went from ${from} to ${to}`,
        punished: "and your opponent punished it",
        inTheGame: (move: string, took: string, how: string, then: string) => `In the game you played ${move}${took}${how}, ${then}.`,
        boardBetter: (text: string, side: string) => `${text}, ${side} is better`,
        boardLevel: (text: string) => `${text}, level`,
        tryBest: (san: string) => `${san} is the move.`,
        tryKeeps: (san: string, after: string) => `${san} keeps you ahead (${after}), but there is a stronger move.`,
        trySlips: (san: string, after: string) => `${san} lets most of it go: ${after} after it.`,
        tryLoses: (san: string, after: string) => `${san} turns it round: ${after} after it.`,
        thinkNote: (thinkSeconds: string, gameSeconds: string | null) =>
            `You answered in ${thinkSeconds} s.${gameSeconds !== null ? ` In the game you took ${gameSeconds} s.` : ""} Take longer: look at what your opponent can do before you move.`,

        // The tables of the result
        tryN: (n: number) => `Try ${n}`,
        tempoImpulsive: "without looking",
        tempoConsidered: "long think",
        tempoTrouble: "short of time",
        colMove: "Move",
        colScore: "Score",
        colLost: "vs best",
        colTime: "Time",
        colBest: "Best",
        colReply: "Reply",
        theMistake: "Here it went",
        noMistake: "No move of yours gave away a mistake's worth: the score slipped a little at a time.",
        rowTitle: "Show this position on the board",
        takeBack: "Take back",
        takeBackTitle: "Takes your last move back. A move taken back counts as missed.",
        tookBack: "You took a move back: it counts as missed, and the position comes back tomorrow.",
        ratingMoves: "Rating your moves…",

        // pages/positionsGame.ts
        nothingReady: "Nothing is ready in this exercise right now.",
        engineFailed: "The engine could not rate the move. Try again.",
        yourMove: "Your move",
        inTheGameLabel: "In the game",
        partnerFailed: "Your partner could not find a move.",
        yourGame: "your game",
        onStudyList: "On your study list",
        onStudyListText: "The line, with the better move, is on your study list.",
        notAdded: "Not added",

        // The engine card
        engine: "Engine",
        depth: (depth: number) => `Stockfish · depth ${depth}`,
        notAvailable: "not available",
        engineThinking: "thinking…",
        whiteBetter: "White is better",
        blackBetter: "Black is better",
    },
    es: {
        findTitle: "Encuentra la jugada",
        findSummary: "La posición antes de un error grave tuyo. Encuentra una jugada mejor que la que hiciste.",
        findPrompt: "Encuentra una jugada mejor que la que hiciste.",
        convertTitle: "Conserva la ventaja",
        convertSummary: "Una posición que tenías ganada y dejaste escapar. Consérvala durante cinco jugadas contra un rival más fuerte.",
        convertPrompt: "Vas ganando. Consérvalo durante cinco jugadas.",
        openingTitle: "Aperturas que se torcieron",
        openingSummary: "El momento en que se torció tu apertura, con tu partida jugada hasta ahí. ¿Qué tendrías que haber jugado?",
        openingPrompt: "Tu apertura se torció aquí. ¿Qué juegas?",

        yourPositions: "Tus posiciones",
        pickerLead: "Los momentos de tus propias partidas analizadas en los que se torció. Cada uno vuelve hasta que lo aciertas dos veces.",
        nothingYet: "Todavía no hay nada que entrenar: salen de tus partidas analizadas.",
        analyseGames: "Analiza tus partidas",
        ready: (n: number) => `${n} ${s(n, "lista", "listas")}`,
        comingBack: (n: number) => `${n} ${s(n, "vuelve", "vuelven")} más adelante`,
        done: (n: number) => `${n} ${s(n, "terminada", "terminadas")}`,
        withClock: "Con el reloj que tenías en la partida",
        start: (n: number) => `Empezar ${n || ""} ${s(n, "posición", "posiciones")}`,

        phaseAll: "Todas",
        phaseOpening: "Apertura",
        phaseMiddlegame: "Medio juego",
        phaseEndgame: "Final",
        phaseTitle: "Solo las posiciones de esta fase de la partida",

        engineScoreTitle: "La evaluación del motor en la partida, desde el lado de las blancas",
        better: (side: string) => `${side} mejor`,
        level: "Igualado",
        youAre: (side: string) => `juegas con ${side.toLowerCase()}`,
        clockTitle: "Tu reloj en la partida, antes de la jugada",
        onYourClock: (clock: string) => `${clock} en tu reloj`,
        thinking: (seconds: number) => `Pensando ${seconds} s`,
        of: (index: number, total: number) => `${index} de ${total}`,
        toMove: (side: string) => `Juegan ${side.toLowerCase()}.`,
        againstPartner: (partner: string) => ` Contra un sparring de nivel ${({
            Beginner: "principiante", Casual: "aficionado", Club: "club", Strong: "fuerte", Expert: "experto",
            "Full strength": "máximo",
        } as Record<string, string>)[partner] ?? partner.toLowerCase()}.`,
        movesLeft: "Jugadas que quedan",
        yourClock: "Tu reloj",
        lookingAtIt: "Mirándola…",
        tryAgain: (n: number) => `Vuelve a intentarlo en el tablero: ${n === 1 ? "te queda 1 intento" : `te quedan ${n} intentos`}.`,
        showSolution: "Ver la solución",
        found: "Encontrada",
        close: "Casi",
        missed: "Fallada",
        tries: (n: number) => `${n} intentos`,
        thought: (seconds: string) => `pensado ${seconds} s`,
        youPlayed: "Jugaste",
        givenAway: (pawns: string) => `(${pawns} perdidos)`,
        best: "La mejor",
        endedAt: "Acabó en",
        hadToStay: (pawns: string) => `(tenía que quedarse en ${pawns} o más a tu favor)`,
        legend: "En el tablero: en verde la mejor jugada, en rojo la de la partida y en azul la tuya. Mueve las piezas para mirar: el motor te sigue.",
        seeRound: "Ver la ronda",
        nextPosition: "Siguiente posición",
        tryItAgain: "Intentarlo otra vez",
        backToPosition: "Volver a la posición",
        addToStudyList: "Añadir la línea a mi lista de estudio",
        leaveRound: "Dejar la ronda",
        roundOver: (title: string) => `${title}: ronda terminada`,
        foundOf: (solved: number, total: number, xp: number) => `${solved} de ${total} encontradas. +${xp} XP.`,
        comeBack: "Las que fallaste vuelven mañana; las que encontraste, dentro de tres días. Dos seguidas y están terminadas.",
        anotherRound: "Otra ronda",
        anotherExercise: "Elegir otro ejercicio",
        thisPosition: "esta posición",
        reading: "Leyendo tus posiciones…",
        signedOutLead: "Los momentos de tus propias partidas analizadas en los que se torció, como ejercicios.",
        logIn: "Inicia sesión",
        toTrain: " para entrenar las tuyas.",

        yourOpponent: "tu rival",
        context: (who: string, when: string | null, speed: string | null) =>
            `vs ${who}${when ? ` · ${when}` : ""}${speed ? ` · ${speed}` : ""}`,
        tookIn: (seconds: string) => ` en ${seconds} s`,
        withoutLooking: ", sin pensarla",
        longThink: ", después de pensarla mucho",
        shortOfTime: ", con poco tiempo",
        notSeen: "y tu rival no lo vio",
        scoreWent: (from: string, to: string) => `y la evaluación pasó de ${from} a ${to}`,
        punished: "y tu rival lo castigó",
        inTheGame: (move: string, took: string, how: string, then: string) => `En la partida jugaste ${move}${took}${how}, ${then}.`,
        boardBetter: (text: string, side: string) => `${text}, ${side.toLowerCase()} mejor`,
        boardLevel: (text: string) => `${text}, igualado`,
        tryBest: (san: string) => `${san} es la jugada.`,
        tryKeeps: (san: string, after: string) => `${san} te mantiene por delante (${after}), pero hay una jugada más fuerte.`,
        trySlips: (san: string, after: string) => `${san} deja escapar casi toda la ventaja: ${after} después.`,
        tryLoses: (san: string, after: string) => `${san} le da la vuelta: ${after} después.`,
        thinkNote: (thinkSeconds: string, gameSeconds: string | null) =>
            `Respondiste en ${thinkSeconds} s.${gameSeconds !== null ? ` En la partida tardaste ${gameSeconds} s.` : ""} Tómate más tiempo: mira qué puede hacer tu rival antes de mover.`,

        tryN: (n: number) => `Intento ${n}`,
        tempoImpulsive: "sin pensarla",
        tempoConsidered: "pensada mucho",
        tempoTrouble: "con poco tiempo",
        colMove: "Jugada",
        colScore: "Evaluación",
        colLost: "vs mejor",
        colTime: "Tiempo",
        colBest: "Mejor",
        colReply: "Respuesta",
        theMistake: "Aquí se fue",
        noMistake: "Ninguna jugada tuya perdió lo que vale un error: la evaluación bajó poco a poco.",
        rowTitle: "Ver esta posición en el tablero",
        takeBack: "Deshacer",
        takeBackTitle: "Deshace tu última jugada. Deshacer cuenta como fallo.",
        tookBack: "Deshiciste una jugada: cuenta como fallo y la posición vuelve mañana.",
        ratingMoves: "Valorando tus jugadas…",

        nothingReady: "Ahora mismo no hay nada listo en este ejercicio.",
        engineFailed: "El motor no ha podido valorar la jugada. Inténtalo de nuevo.",
        yourMove: "Tu jugada",
        inTheGameLabel: "En la partida",
        partnerFailed: "Tu sparring no ha encontrado jugada.",
        yourGame: "tu partida",
        onStudyList: "En tu lista de estudio",
        onStudyListText: "La línea, con la jugada mejor, está en tu lista de estudio.",
        notAdded: "No se ha añadido",

        engine: "Motor",
        depth: (depth: number) => `Stockfish · profundidad ${depth}`,
        notAvailable: "no disponible",
        engineThinking: "pensando…",
        whiteBetter: "Blancas mejor",
        blackBetter: "Negras mejor",
    },
});
